import React, { useState } from 'react';
import Papa from 'papaparse';
import ApiClient from "../utils/ApiClient";
import ConfirmationModalCommon from "../components/Common/ConfirmationModalCommon";

const UploadPo = () => {
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [parsedData, setParsedData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [dragCounter, setDragCounter] = useState(0);

    const preventDefaults = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (e) => {
        preventDefaults(e);
        setDragCounter(prev => prev + 1);
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        preventDefaults(e);
        setDragCounter(prev => prev - 1);
        if (dragCounter === 1) {
            setIsDragging(false);
        }
    };

    const handleDrop = (e) => {
        preventDefaults(e);
        setIsDragging(false);
        setDragCounter(0);
        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
    };

    const handleFiles = async (files) => {
        const csvFiles = files.filter(
            (file) => file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.txt')
        );
        if (csvFiles.length === 0) {
            console.warn('No valid files found.');
            return;
        }

        try {
            const processedData = await Promise.all(csvFiles.map(parseFile));
            setUploadedFiles((prev) => [...prev, ...csvFiles]);
            setParsedData((prev) => [...prev, ...processedData.flat()]);
        } catch (error) {
            console.error('Error processing files:', error);
        }
    };

    const parseFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const text = event.target.result;
                Papa.parse(text, {
                    header: true,
                    skipEmptyLines: 'greedy',
                    dynamicTyping: false,
                    transformHeader: (header) => header.trim(),
                    complete: function(results) {
                        if (results.errors.length > 0) {
                            reject(new Error('Error parsing CSV: ' + results.errors[0].message));
                            return;
                        }

                        const transformedData = results.data.map(row => {
                            const newRow = {};
                            Object.entries(row).forEach(([key, value]) => {
                                const trimmedValue = value.trim();
                                if (!isNaN(trimmedValue) && trimmedValue !== '') {
                                    newRow[key] = parseFloat(trimmedValue);
                                } else {
                                    newRow[key] = trimmedValue;
                                }
                            });
                            return newRow;
                        });

                        resolve(transformedData);
                    },
                    error: function(error) {
                        reject(error);
                    }
                });
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsText(file);
        });
    };

    const handleSubmit = () => {
        try {
            ApiClient.post("/api/uploadpos", {
                data: parsedData,
                fileName: uploadedFiles.map((file) => file.name).join(', ')
            })
                .then(response => {
                    setModalMessage(response.data === 1 ? 'PO uploaded successfully' : 'Duplicate PO or System Error');
                    setShowModal(true);
                })
                .catch(error => {
                    console.error('Network error:', error);
                    setModalMessage('Upload Failed - Duplicate PO or Error');
                    setShowModal(true);
                });
        } catch (error) {
            console.error('Upload failed:', error);
            setModalMessage(`Upload failed: ${error.message}`);
            setShowModal(true);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div
                className={`border-dashed border-2 ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} 
               rounded-lg p-6 text-center cursor-pointer transition-colors duration-200`}
                onDrop={handleDrop}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={preventDefaults}
                onClick={() => document.getElementById('fileInput').click()}
            >
                <p className="text-lg font-semibold">
                    {isDragging ? 'Drop files here' : 'Drag and drop files here, or click to select files'}
                </p>
                <input
                    id="fileInput"
                    type="file"
                    multiple
                    className="hidden"
                    accept=".csv,.txt"
                    onChange={(e) => handleFiles(Array.from(e.target.files))}
                />
            </div>

            <div className="mt-6">
                <h3 className="text-lg font-semibold">Uploaded Files:</h3>
                <ul className="list-disc list-inside">
                    {Array.isArray(uploadedFiles) && uploadedFiles.length > 0 ? (
                        uploadedFiles.map((file, index) => (
                            <li key={index} className="text-gray-600">
                                {typeof file?.name === 'string' ? file.name : 'Unnamed File'}
                            </li>
                        ))
                    ) : (
                        <p className="text-gray-500">No files uploaded yet.</p>
                    )}
                </ul>
            </div>

            <button
                className="mt-4 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
                onClick={handleSubmit}
                disabled={parsedData.length === 0}
            >
                Submit to Backend
            </button>

            {showModal && (
                <ConfirmationModalCommon
                    open={showModal}
                    setOpen={(open) => {
                        setShowModal(open);
                        if (!open) {
                            setUploadedFiles([]);
                            setParsedData([]);
                        }
                    }}
                    message={modalMessage}
                    isSuccess={modalMessage === 'PO uploaded successfully'}
                />
            )}
        </div>
    );
};

export default UploadPo;